import { ArrowsClockwise } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { Button } from "common/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";

import useInboxRefresh from "../../../datahooks/useInboxRefresh";
import useActiveConversationOwnerAccount from "../../../hooks/useActiveConversationOwnerAccount";
import { formatConversationMessagesDate } from "../../../utils";

const COOLDOWN_MS = 15 * 60 * 1000;

export default function SyncButton({ lastSyncAt }: { lastSyncAt: number }) {
  const { ownerAccountId, ownerAccount } = useActiveConversationOwnerAccount();

  const [isSyncing, setIsSyncing] = useState(false);
  const [, setTick] = useState(crypto.randomUUID());

  const timeoutId = useRef<number>();

  const { refreshInbox } = useInboxRefresh();

  useEffect(() => {
    if (lastSyncAt) {
      clearTimeout(timeoutId.current);
      setIsSyncing(false);

      const msSinceLastSync = Date.now() - lastSyncAt;
      if (msSinceLastSync < COOLDOWN_MS) {
        const intervalId = setInterval(
          () => setTick(crypto.randomUUID()),
          1000,
        );
        return () => clearInterval(intervalId);
      }
    }
  }, [lastSyncAt]);

  //prevent the toast from appearing if the component unmounts
  useEffect(() => () => clearTimeout(timeoutId.current), []);

  function onClick() {
    setIsSyncing(true);
    refreshInbox({ accountId: ownerAccountId })
      .then(() => {
        //timeout in case the ws event takes too long
        timeoutId.current = window.setTimeout(() => {
          setIsSyncing(false);
          toast.error(
            "Inbox syncing is taking some time. Please try again later",
          );
        }, 10000);
      })
      .catch(() => {
        setIsSyncing(false);
      });
  }

  let tooltipContent = "Sync conversation with LinkedIn";
  let isDisabled: boolean;

  if (isSyncing) {
    tooltipContent = "Syncing...";
    isDisabled = true;
  } else if (lastSyncAt) {
    const lastSyncAtDate = new Date(lastSyncAt);
    const msSinceLastSync = Date.now() - lastSyncAt;

    if (msSinceLastSync < COOLDOWN_MS) {
      isDisabled = true;
      const msRemaining = COOLDOWN_MS - msSinceLastSync;
      const minutes = Math.floor(msRemaining / 60000);
      const seconds = Math.floor((msRemaining % 60000) / 1000);
      tooltipContent = `Available in ${minutes}:${seconds.toString().padStart(2, "0")}`;
    } else {
      isDisabled = false;
      tooltipContent += `.\nLast message detected ${formatConversationMessagesDate(lastSyncAtDate)}`;
    }
  }
  //workspace account state
  if (
    ownerAccount &&
    ((!ownerAccount.license && ownerAccount.type !== "rental") ||
      ownerAccount.state !== "LoggedIn")
  ) {
    isDisabled = true;
    tooltipContent =
      ownerAccount.state !== "LoggedIn"
        ? "Reconnect your LinkedIn account to sync your inbox"
        : "Assign a seat to this account to sync your inbox";
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="secondary-black"
          size="sm"
          intent="iconOnly"
          onClick={onClick}
          disabled={isDisabled}
        >
          <ArrowsClockwise className={isSyncing && "animate-spin"} />
        </Button>
      </TooltipTrigger>
      <TooltipContent className="">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
}
